import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  HbrDomTable,
  HbrIcon,
  HbrInput,
  HbrPagination,
  HbrButton,
} from '@harbor/elements-react-standalone';
import {
  caretRight,
  magnifyingGlass,
  statusInfoSquare,
  statusWarning,
} from '@harbor/elements/icons';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { useUserRegistry } from '../../hooks/useUserRegistry';
import { defaultColumns, defaultOrderBy } from '../../support/config/tables';
import {
  fetchSalesforceCustomers,
  setPage,
  setSearch,
  setSize,
} from '../../support/slices/salesforceCustomersSlice';
import { RootState, useAppDispatch } from '../../support/store';

import { Column, ColumnPin, ColumnType, getColumnTypeConfig } from '../../types/table';
import { UserView, UserViewType } from '../../types/user';
import TableColumnManagerModal from '../Common/TableColumnManagerModal';
import StbTableFilters from '../Common/TableView/StbTableFilters';
import TABLE_STYLES from '../Common/TableView/table-styles';
import StbLoader from '../security-toolbox-ui/StbLoader';
import thTemplate from '../Common/TableView/th-templates';
import { SfRecordType } from '../../types/salesforce';
import { asSingleValue } from '../../support/utils';

interface CustomerTableProps {
  view?: UserView;
  branch: string | null;
}

const TABLE_RIGHT_BUFFER = 10;
const TABLE_WIDTH_SAFETY_BUFFER = 2;
const TABLE_SCROLL_ACTIVATION_THRESHOLD = 40;

const getColumnWidth = (column: Column) => {
  if (typeof column.width === 'number') return column.width;
  if (typeof column.minWidth === 'number') return column.minWidth;

  switch (column.id) {
    case 'Name':
      return 320;
    default:
      return 160;
  }
};

const getNaturalTableWidth = (columns: Column[]) => {
  return columns.reduce((total, column) => total + getColumnWidth(column), 0);
};

type CustomerNode = Record<string, any> & {
  id: string;
  Name?: string;
  children?: CustomerNode[];
};

const normalizeNode = (node: any): CustomerNode => {
  const id = node?.id ?? node?.Id;
  const Name = node?.Name ?? node?.name;

  return {
    ...node,
    id,
    Name,
    children: (node?.children || []).map((c: any) => normalizeNode(c)),
  };
};

const CustomerTable: React.FC<CustomerTableProps> = ({ view }) => {
  const { updateViewColumn, updateViewColumns, updateViewOrderBy } = useUserRegistry();
  const dispatch = useAppDispatch();

  const customersRaw = useSelector(
    (state: RootState) => state.salesforceCustomers.views[view?.id]?.customers
  );

  const customersTree: CustomerNode[] = useMemo(() => {
    return (customersRaw ?? []).map((r: any) => normalizeNode(r));
  }, [customersRaw]);

  const customersInitialized = useSelector(
    (state: RootState) => state.salesforceCustomers.initialLoadComplete
  );

  const totalCount = useSelector(
    (state: RootState) => state.salesforceCustomers.views[view?.id]?.totalCount ?? 0
  );
  const pageIndex = useSelector(
    (state: RootState) => state.salesforceCustomers.views[view?.id]?.page ?? 0
  );
  const size = useSelector(
    (state: RootState) => state.salesforceCustomers.views[view?.id]?.size ?? 30
  );
  const search = useSelector(
    (state: RootState) => state.salesforceCustomers.views[view?.id]?.search ?? ''
  );
  const viewLoading = useSelector(
    (state: RootState) => state.salesforceCustomers.loading ?? false
  );
  const error = useSelector(
    (state: RootState) => state.salesforceCustomers.error ?? null
  );

  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);

  const [visibleColumns, setVisibleColumns] = useState<Column[]>([]);
  const [columnManagerOpen, setColumnManagerOpen] = useState<boolean>(false);

  const [treeOpen, setTreeOpen] = useState<string[]>([]);
  const [expanded] = useState<string[]>([]);
  const [selection, setSelection] = useState<string[]>([]);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [forceRenderKey, setForceRenderKey] = useState(0);

  const pageRef = useRef<HTMLDivElement | null>(null);
  const [pageWidth, setPageWidth] = useState(0);

  useLayoutEffect(() => {
    const element = pageRef.current;
    if (!element) return;

    const getAvailableWidth = () => {
      const appContent = element.closest('.app-content') as HTMLElement | null;

      return (
        appContent?.clientWidth ||
        element.clientWidth ||
        document.documentElement.clientWidth ||
        window.innerWidth ||
        0
      );
    };

    const update = () => {
      setPageWidth(getAvailableWidth());
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(element);

    const appContent = element.closest('.app-content') as HTMLElement | null;
    if (appContent) {
      observer.observe(appContent);
    }

    window.addEventListener('resize', update, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  const tableSizing = useMemo(() => {
    const available = Math.max(
      320,
      pageWidth - TABLE_RIGHT_BUFFER - TABLE_WIDTH_SAFETY_BUFFER
    );

    const natural = getNaturalTableWidth(visibleColumns);

    const shouldOverflow =
      natural > available + TABLE_SCROLL_ACTIVATION_THRESHOLD;

    return {
      shouldOverflow,
      tableWidth: shouldOverflow ? natural : undefined,
      availableWidth: available,
    };
  }, [pageWidth, visibleColumns]);

  const tableSurfaceStyle = useMemo<React.CSSProperties>(() => {
    if (tableSizing.shouldOverflow && tableSizing.tableWidth) {
      return {
        width: `${tableSizing.tableWidth}px`,
        maxWidth: 'none',
      };
    }

    return {
      width: `calc(100% - ${TABLE_RIGHT_BUFFER}px)`,
      maxWidth: `calc(100% - ${TABLE_RIGHT_BUFFER}px)`,
    };
  }, [tableSizing]);

  const tableStyle = useMemo<React.CSSProperties>(() => {
    if (tableSizing.shouldOverflow && tableSizing.tableWidth) {
      return {
        width: `${tableSizing.tableWidth}px`,
        minWidth: `${tableSizing.tableWidth}px`,
        maxWidth: 'none',
      };
    }

    return {
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
    };
  }, [tableSizing]);

  const refreshCustomers = useCallback(async () => {
    if (!view?.id) return;

    try {
      dispatch(fetchSalesforceCustomers({ view }));
    } catch (e) {
      console.error('Error fetching Customers data:', e);
    }
  }, [dispatch, view]);

  useEffect(() => {
    if (!view?.id) return;
    refreshCustomers();
  }, [view, refreshCustomers]);

  const initializeVisibleColumns = useCallback(
    (
      columns: Column[] = defaultColumns.customer,
      order:
        | { column: string; order: 'asc' | 'desc' }
        | undefined = defaultOrderBy.customer,
    ) => {
      return [
        {
          id: 'Name',
          name: 'Customer',
          width: 300,
          columnType: ColumnType.Custom,
          sortable: true,
          order: order?.column === 'Name' ? order.order : undefined,
          reorderable: false,
          pin: ColumnPin.Left,
        },
        ...columns.map((column) => ({
          ...column,
          name: column?.name,
          columnType: column.columnType || ColumnType.String,
          order: order?.column === column.id ? order.order : undefined,
        })),
      ];
    },
    [],
  );

  useEffect(() => {
    const columns = (view?.columns || defaultColumns.customer).map((column) =>
      getColumnTypeConfig(column),
    );

    setVisibleColumns(
      initializeVisibleColumns(columns, view?.orderBy ?? defaultOrderBy.customer),
    );
  }, [view, initializeVisibleColumns]);

  useEffect(() => {
    setSelectedRowId(null);
    setSelection([]);
    setTreeOpen([]);
    setForceRenderKey((k) => k + 1);
  }, [customersTree]);

  useLayoutEffect(() => {
    if (viewLoading) {
      setIsLoadingCustomers(true);
    } else {
      const timeout = setTimeout(() => {
        setIsLoadingCustomers(false);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [viewLoading]);

  const handleSearchChange = (searchValue: string) => {
    const trimmedValue = searchValue.trim();
    if (!view?.id) return;

    dispatch(setPage({ viewId: view.id, page: 0 }));
    dispatch(setSearch({ viewId: view.id, search: trimmedValue }));
    refreshCustomers();
  };

  const handleCancelClick = async () => {
    setColumnManagerOpen(false);
  };

  const sanitizeColumns = (columns: unknown[]) => {
    return (columns as any[])
      .filter((col: any) => !['Name'].includes(col.id))
      .map((col: any) => {
        const cleanedCol = { ...col };
        Object.keys(cleanedCol).forEach((key) => {
          if (typeof cleanedCol[key] === 'function') {
            delete cleanedCol[key];
          }
        });
        return cleanedCol;
      });
  };

  const handleSetVisibleColumns = (selectedColumns: Column[]) => {
    if (!view?.id) return;

    const updatedColumns = selectedColumns.map((col) => {
      const found = visibleColumns.find((selected) => selected.id === col.id);
      return found ?? getColumnTypeConfig(col);
    });

    setVisibleColumns(updatedColumns);
    updateViewColumns(UserViewType.Customer, view.id, sanitizeColumns(updatedColumns));
    setColumnManagerOpen(false);
  };

  const handleChangePage = (chunk: number) => {
    if (!view?.id) return;
    dispatch(setPage({ viewId: view.id, page: chunk }));
    refreshCustomers();
  };

  const handleChangeRowsPerPage = (newSize: number) => {
    if (!view?.id) return;
    dispatch(setSize({ viewId: view.id, size: newSize }));
    refreshCustomers();
  };

  const handleSetViewFilters = (_filters: any) => {
    if (!view?.id) return;
    dispatch(setPage({ viewId: view.id, page: 0 }));
  };

  const renderHeader = () => {
    return visibleColumns.map((column) =>
      thTemplate(column, visibleColumns, SfRecordType.Customer, handleSetVisibleColumns),
    );
  };

  const handleSort = (e: any) => {
    if (!view?.id) return;

    const { order, column } = e.detail;
    updateViewOrderBy(UserViewType.Customer, view.id, {
      column: column.id,
      order,
    });

    refreshCustomers();
    setForceRenderKey((k) => k + 1);
  };

  const renderBody = () => {
    if (!customersTree || customersTree.length === 0) return null;

    const list: JSX.Element[] = [];

    const renderNode = (node: CustomerNode, level: number) => {
      const isOpen = treeOpen.includes(node.id);
      const isSelected = selectedRowId === node.id;
      const hasChildren = !!(node.children && node.children.length);

      visibleColumns.forEach((column) => {
        const slot = `row-${node.id}-column-${column.id}`;

        if (column.id === 'Name') {
          const baseStyle: React.CSSProperties = {
            paddingLeft: `${hasChildren ? level * 24 : level * 24 + 24}px`,
            cursor: 'pointer',
            backgroundColor: isSelected ? 'var(--hbr-color-bg-info-soft, #eef6ff)' : undefined,
          };

          list.push(
            <div
              key={slot}
              slot={slot}
              className="min-h-6 flex items-center"
              style={{ ...baseStyle, display: 'flex', gap: '4px', alignItems: 'center' }}
              onClick={() => setSelectedRowId(node.id)}
            >
              {hasChildren && (
                <HbrButton
                  variant="text"
                  size="xsmall"
                  aria={{ label: 'expandable row' }}
                  onClick={(evt: any) => {
                    evt.stopPropagation();
                    setSelectedRowId(node.id);

                    setTreeOpen((prev) =>
                      prev.includes(node.id)
                        ? prev.filter((id) => id !== node.id)
                        : [...prev, node.id],
                    );
                  }}
                >
                  <HbrIcon
                    asset={caretRight}
                    className={isOpen ? 'hbr-css__expandable-btn-expanded' : ''}
                  />
                </HbrButton>
              )}

              <div className="box-content font-semibold hover:text-link hover:underline cursor-pointer min-h-6 flex items-center">
                <Link to={`/customer/${node.id}`}>
                  {node.Name ?? node.name ?? node.id}
                </Link>
              </div>
            </div>,
          );
          return;
        }

        const value = (node as any)[column.id];
        list.push(
          <div
            key={slot}
            slot={slot}
            className="min-h-6 flex items-center"
            onClick={() => setSelectedRowId(node.id)}
          >
            {column.format ? column.format(value) : value ?? '-'}
          </div>,
        );
      });

      if (isOpen && node.children && node.children.length) {
        node.children.forEach((child) => renderNode(child, level + 1));
      }
    };

    customersTree.forEach((root) => renderNode(root, 0));
    return list;
  };

  return (
    <div className="salesforce-table-page" ref={pageRef}>
      <div className="salesforce-table-header">
        <div className={`${TABLE_STYLES.filtersGrid} ${TABLE_STYLES.filtersCust}`}>
          <div className="min-w-0 w-full h-full flex items-center">
            {customersInitialized && (
              <HbrInput
                placeholder="Search customers"
                value={search}
                size="large"
                clearable={true}
                onHbr-change={(e) => handleSearchChange(asSingleValue(e.target.value))}
                className="mt-2 ml-0 lg:mt-0.5 w-full"
              >
                <HbrIcon
                  slot="prefix"
                  name="magnifying-glass"
                  asset={magnifyingGlass}
                  size="16px"
                  staticColors={true}
                />
              </HbrInput>
            )}
          </div>

          <div />

          <div className="md:col-span-2 xl:col-span-1 min-w-0 w-full">
            <StbTableFilters
              userView={view as UserView}
              setViewFilters={handleSetViewFilters}
              visibleColumns={visibleColumns}
            />
          </div>
        </div>
      </div>

      {customersInitialized && !error && !isLoadingCustomers && customersTree.length === 0 && (
        <div className="salesforce-table-header">
          <div className="bg-blue-100 border border-blue-200 text-blue-800 px-4 py-3 rounded-md mb-4 flex items-center gap-2">
            <HbrIcon asset={statusInfoSquare} sentiment="info" />
            No customers found
          </div>
        </div>
      )}

      {customersInitialized && error === 'Unauthorized' && (
        <div className="salesforce-table-header">
          <div className="bg-red-100 border border-red-200 text-red-800 px-4 py-3 rounded-md mb-4 flex items-center gap-2">
            <HbrIcon asset={statusWarning} sentiment="warning" />
            SalesForce session expired. Please log in again.
          </div>
        </div>
      )}

      {customersTree.length > 0 && (
        <div
          className="salesforce-table-surface"
          style={tableSurfaceStyle}
        >
          <HbrDomTable
            key={forceRenderKey}
            tree={true}
            autoLayout={true}
            expanded={expanded}
            opened={treeOpen}
            selected={selection}
            columns={visibleColumns}
            data={customersTree}
            style={tableStyle}
            onHbr-sort={handleSort}
            onHbr-column-reorder={(e: any) => {
              if (!view?.id) return;
              if (e.detail.columns) {
                setVisibleColumns(e.detail.columns);
                updateViewColumns(
                  UserViewType.Customer,
                  view.id,
                  sanitizeColumns(e.detail.columns),
                );
              }
            }}
            onHbr-column-resize={(e: any) => {
              if (!view?.id) return;
              updateViewColumn(
                UserViewType.Customer,
                view.id,
                e.detail.column.id,
                'width',
                e.detail.column.width,
              );
            }}
          >
            {renderHeader()}
            {renderBody()}
          </HbrDomTable>
        </div>
      )}

      <div className="salesforce-table-header">
        <div className={`${TABLE_STYLES.pagination} py-2 mb-0`}>
          <HbrPagination
            itemCount={totalCount}
            pageSize={size}
            page={pageIndex}
            sizeChanger={true}
            onHbr-size-changed={(e: any) => handleChangeRowsPerPage(e.detail.pageSize)}
            onHbr-page-changed={(e: any) => handleChangePage(e.detail)}
          />
        </div>
      </div>

      {columnManagerOpen && (
        <TableColumnManagerModal
          modalOpen={columnManagerOpen}
          cancelSetColumns={handleCancelClick}
          columns={visibleColumns.map(({ id, name }) => ({ id, name }))}
          setColumns={handleSetVisibleColumns}
        />
      )}

      <StbLoader isLoading={isLoadingCustomers} />
    </div>
  );
};

export default CustomerTable;